<!-- eslint-disable no-debugger -->
<template>
  <!-- eslint-disable max-len -->
  <div>
    <Banner
      v-if="showOutageBanner"
      class="c-banner--alert"
      role="alert"
    >
      <span class="font-bold block">
        THE USPS® CHANGE OF ADDRESS WEBSITE IS TEMPORARILY UNAVAILABLE.
      </span>
      We are currently unable to service your online Change-of-Address.
      We apologize for the inconvenience, please try again later.
    </Banner>
    <Banner
      v-if="showUpcomingMaintenanceBanner"
      class="c-banner--alert"
      role="alert"
    >
      {{ showUpcomingMaintenanceBanner }}
    </Banner>
    <main
      id="main"
      ref="main"
      class="disclaimer-desktop"
    >
      <div class="w-screen bg-blue-200">
        <div class="flex items-center u-container--x coac-link">
          <div class="text-xl text-white mr-2">
            <a
              id="view-edit-coac"
              :href="$options.coacUrl"
              title="Official USPS Change-of-Address Correct Form"
              class="font-bold"
            >
              Manage your Change-of-Address® on managemymove.com
            </a>
          </div>
          <a
            :href="$options.coacUrl"
            title="Official USPS Change-of-Address Correct Form"
            class="not-sr-only"
          >
            <span class="red-arrow" />
          </a>
        </div>
      </div>
      <form
        ref="form"
        novalidate
        class="u-container pb-0"
        @submit.prevent="validate(); analytics.form.submitted();"
      >
        <SimplifyHero />
        <IndividualProgressBarWithSteps
          role="progressbar"
          :mobile="false"
        />
        <section id="exp-inline-choice" />
        <div class="c-hairline" />
        <section
          id="get-started"
          ref="getStarted"
          class="flex py-8"
        >
          <div class="w-1/2 max:pr-xl">
            <Tooltip
              placement="top"
              @show="analytics.click.tooltipMoveType"
            >
              <h3
                id="step-heading"
                ref="stepHeading"
                class="mb-4"
              >
                <span class="font-bold">
                  Type of Address Change
                </span>
              </h3>
              <template slot="tip">
                <p>
                  <span class="font-bold">Individual:</span> Choose this option if one person is moving. If you receive mail by more than one name (i.e. maiden name or nickname), complete a separate form for each name.
                </p>
                <p class="mt-3">
                  <span class="font-bold">Family:</span> Choose this option and complete one form if everyone in your family has the same last name. If not all family members are moving, OR if some family members have different last names, choose the Individual option and submit a separate order for each mover.
                </p>
                <p class="mt-3">
                  <span class="font-bold">Business:</span> Choose this option if your business is moving. Individual or Family orders that included a business address as the old address will not be processed. Existing or former employees cannot have their "personal" mail forwarded from a business.
                </p>
              </template>
              <template slot="accessible">
                Mover types description
              </template>
            </Tooltip>
            <fieldset>
              <SimplifyInputRadio
                ref="individual"
                v-model="moverType"
                title="Individual"
                description="You are the only person moving."
                class="simplify-mover-type-button"
                form-value="INDIVIDUAL"
                name="mover-type"
                button
                :disabled="ivsProofingCompleted || disableNonBusinessMover"
                @input="analytics.click.moverType('INDIVIDUAL')"
              >
                <template #icon>
                  <SvgIndividual
                    class="mx-auto mover-type-icon"
                  />
                </template>
              </SimplifyInputRadio>

              <SimplifyInputRadio
                ref="family"
                v-model="moverType"
                title="Family"
                description="Everyone in your family moving."
                class="simplify-mover-type-button"
                form-value="FAMILY"
                name="mover-type"
                button
                :disabled="ivsProofingCompleted || disableNonBusinessMover"
                @input="analytics.click.moverType('FAMILY')"
              >
                <template #icon>
                  <SvgFamily
                    class="mx-auto mover-type-icon"
                  />
                </template>
              </SimplifyInputRadio>

              <SimplifyInputRadio
                ref="business"
                v-model="moverType"
                title="Business"
                description="Your business is moving."
                class="simplify-mover-type-button"
                form-value="BUSINESS"
                button
                name="mover-type"
                :disabled="ivsProofingCompleted"
                @input="analytics.click.moverType('BUSINESS')"
              >
                <template #icon>
                  <SvgBusiness
                    class="mx-auto mover-type-icon"
                  />
                </template>
              </SimplifyInputRadio>
            </fieldset>
            <p
              v-if="errors.moverType.hasError"
              id="moverType-error"
              class="errors text-red-200 text-sm font-bold"
            >
              {{ errors.moverType.message }}
            </p>
            <template v-if="moverType === 'FAMILY'">
              <fieldset
                ref="familySameLastName"
                class="mt-6 flex flex-row"
                role="group"
                aria-labelledby="family-same-last-name"
                @keyup.tab="$refs.familyRemaining.focus();
                            familyRemaining='NO'"
              >
                <p
                  id="family-same-last-name"
                  class="flex-auto c-input-label mr-3"
                >
                  Do all individuals moving from this address share the same last name?
                </p>
                <InputRadio
                  v-model="familySameLastName"
                  form-value="YES"
                  name="family-same-last-name"
                  class="flex-shrink"
                  :has-error="errors.familySameLastName.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familySameLastNameHandle('YES'); analytics.input.familySameLastName('YES')"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="familySameLastName"
                  form-value="NO"
                  name="family-same-last-name"
                  class="flex-shrink"
                  :has-error="errors.familySameLastName.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familySameLastNameHandle('NO'); analytics.input.familySameLastName('NO')"
                >
                  No
                </InputRadio>
              </fieldset>
              <p
                :class="{ 'errors': errors.familySameLastName.hasError }"
                class="c-input-helper"
              >
                {{ errors.familySameLastName.message }}
              </p>
              <fieldset
                ref="familyRemaining"
                class="mt-3 flex flex-row"
                role="group"
                aria-labelledby="family-remaining"
              >
                <p
                  id="family-remaining"
                  class="flex-auto c-input-label mr-3"
                >
                  Is anyone with this same last name remaining at the address?
                </p>
                <InputRadio
                  v-model="familyRemaining"
                  form-value="YES"
                  name="family-remaining"
                  class="flex-shrink"
                  tabindex="1"
                  :has-error="errors.familyRemaining.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familyRemainingHandle('YES'); analytics.input.familyRemaining('YES')"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="familyRemaining"
                  form-value="NO"
                  name="family-remaining"
                  class="flex-shrink"
                  tabindex="0"
                  :has-error="errors.familyRemaining.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familyRemainingHandle('NO'); analytics.input.familyRemaining('NO')"
                >
                  No
                </InputRadio>
              </fieldset>
              <p
                :class="{ 'errors': errors.familyRemaining.hasError }"
                class="c-input-helper"
              >
                {{ errors.familyRemaining.message }}
              </p>
            </template>
            <template v-if="moverType === 'BUSINESS'">
              <fieldset
                ref="businessWholeCompany"
                aria-labelledby="business-wholeCompany"
                role="group"
                class="mt-6 flex flex-row"
              >
                <p
                  id="business-wholeCompany"
                  class="flex-auto c-input-label"
                >
                  Is the ENTIRE business moving?
                </p>
                <InputRadio
                  v-model="businessWholeCompany"
                  form-value="YES"
                  name="business-whole-company"
                  class="flex-shrink"
                  :has-error="errors.businessWholeCompany.hasError"
                  @input="businessWholeCompanyHandle('YES'); analytics.input.businessWholeCompany('YES')"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="businessWholeCompany"
                  form-value="NO"
                  name="business-whole-company"
                  class="flex-shrink"
                  :has-error="errors.businessWholeCompany.hasError"
                  :disabled="disableNonBusinessMover"
                  @input="businessWholeCompanyHandle('NO'); analytics.input.businessWholeCompany('NO')"
                >
                  No
                </InputRadio>
              </fieldset>
              <p
                :class="{ 'errors': errors.businessWholeCompany.hasError }"
                class="c-input-helper"
              >
                {{ errors.businessWholeCompany.message }}
              </p>
            </template>
            <template v-if="enhancedBusinessFlowEnabled && moverType === 'BUSINESS'">
              <fieldset
                ref="multipleAddresses"
                aria-labelledby="multiple-addresses"
                role="group"
                class="flex flex-row"
              >
                <p
                  id="multiple-addresses"
                  class="flex-auto c-input-label"
                >
                  Does the business have multiple addresses?
                </p>
                <InputRadio
                  v-model="multipleAddresses"
                  form-value="YES"
                  name="multiple-addresses"
                  class="flex-shrink"
                  :has-error="errors.multipleAddresses.hasError"
                  @input="analytics.input.businessMultipleAddresses('YES')"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="multipleAddresses"
                  form-value="NO"
                  name="multiple-addresses"
                  class="flex-shrink"
                  :has-error="errors.multipleAddresses.hasError"
                  :disabled="disableNonBusinessMover"
                  @input="analytics.input.businessMultipleAddresses('NO')"
                >
                  No
                </InputRadio>
              </fieldset>
              <p
                :class="{ 'errors': errors.multipleAddresses.hasError }"
                class="c-input-helper"
              >
                {{ errors.multipleAddresses.message }}
              </p>
            </template>
          </div>
        </section>
        <div class="c-hairline" />
        <template v-if="multipleAddresses === 'YES'">
          <MultipleBusinessAddresses
            ref="multipleAddressesComponent"
            :is-simplify="simplify"
            :forward-type-val="forwardType"
            :start-date-val="startDate"
            :stop-date-val="stopDate"
            :business-whole-company-val="businessWholeCompany"
            :email-val="email"
            :phone-number-val="phoneNumber"
            view-mode="simplify"
            this.errors
            @validateForm="validateForm()"
            @fieldErrors="(fieldErrors)=>updateFieldErrors(fieldErrors)"
            @fieldErrorsMilitary="updateFieldErrorsMilitary"
            @disableNonBusinessMovers="disableMoverType"
          />
        </template>
        <template v-else>
          <div class="c-hairline" />
          <section
            class="flex py-8 c-hairline-after"
          >
            <div class="w-1/2 max:pr-xl">
              <Tooltip
                placement="top"
                @show="analytics.click.tooltipOldAddress"
              >
                <h3 class="font-bold">
                  Old Mailing Address
                </h3>
                <template slot="tip">
                  <p>
                    This is the address you're moving from. Enter a valid address,
                    and remember to include apartment/suite number if applicable.
                  </p>
                  <p
                    class="mt-5"
                  >
                    If your address is a PO Box, enter it in the Street Address
                    field for example, PO Box 1001. Please note that Change-of-Address
                    requests from non-USPS Private Mail Boxes (PMB) cannot be processed.
                  </p>
                  <p
                    v-if="oldPoBoxException"
                    class="errors text-red-200 mt-5"
                  >
                    <span class="font-bold">Note:</span> The address you provided is not
                    considered a proper mailing address within this ZIP Code™. A proper
                    mailing address within this ZIP Code™ requires a PO Box address.
                    Please input your PO Box number in the “PO Box” field and click "Next"
                    to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
                  </p>
                </template>
              </Tooltip>

              <InputZipCode
                ref="oldZipCode"
                v-model="oldZipCode"
                label="ZIP Code™"
                placeholder="00000"
                name="old-zip-code"
                class="mt-5"
                :choice-experience="true"
                :has-error="errors.oldZipCode.hasError"
                :async-fn="zipSearch({
                  cityName: oldCity,
                  state: oldState,
                })"
                :transform-compare="zipCompare"
                :transform-label="zipLabel"
                :transform-value="zipValue"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldZipCode"
                @select="oldCity = $event.cityName;
                         oldState = $event.state"
              >
                <template slot="helper">
                  {{ errors.oldZipCode.message }}
                </template>
              </InputZipCode>
              <div class="choice__selections--unstack">
                <InputListBox
                  ref="oldCity"
                  v-model="oldCity"
                  label="City"
                  placeholder="City"
                  name="old-city"
                  class="mt-5"
                  :has-error="errors.oldCity.hasError"
                  :async-fn="citySearch({
                    zip: oldZipCode,
                    state: oldState,
                  })"
                  :transform-compare="zipCompare"
                  :transform-label="cityLabel"
                  :transform-value="cityValue"
                  :disabled="ivsProofingCompleted"
                  @blur="analytics.input.oldCity"
                  @select="oldZipCode = $event.zip; oldState = $event.state"
                >
                  {{ errors.oldCity.message }}
                </InputListBox>
                <InputState
                  ref="oldState"
                  v-model="oldState"
                  label="State"
                  name="old-state"
                  class="mt-5"
                  :has-error="errors.oldState.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="analytics.input.oldState()"
                >
                  <span
                    v-if="errors.oldState.hasError"
                    slot="helper"
                  >
                    {{ errors.oldState.message }}
                  </span>
                </InputState>
              </div>
              <InputListBox
                ref="oldStreet"
                v-model="oldStreetAddress"
                label="Street Address"
                name="old-street-address"
                placeholder="Street Address"
                class="mt-5"
                :has-error="errors.oldStreetAddress.hasError && !showOldPoBox"
                :async-fn="streetSearch({
                  cityName: oldCity,
                  state: oldState,
                  zipcode: oldZipCode,
                })"
                :transform-compare="streetCompare"
                :transform-label="streetLabel"
                :transform-value="streetValue"
                :disabled="ivsProofingCompleted"
                @input="oldPoBoxNumber = ''"
                @blur="analytics.input.oldStreet"
                @select="oldZipCode=$event.zip;
                         oldCity=$event.cityName;
                         oldState=$event.state;"
              >
                {{ errors.oldStreetAddress.message }}
              </InputListBox>
              <InputText
                v-if="oldState === 'PR'"
                ref="oldUrbanizationName"
                v-model="oldUrbanizationName"
                label="Urbanization Name (Puerto Rico only)"
                name="old-urbanization-name"
                placeholder="URB HIGHLAND GDNS"
                class="mt-5"
                :has-error="errors.oldUrbanizationName.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldUrbanizationName"
              >
                <span slot="helper">
                  {{ errors.oldUrbanizationName.message }}
                </span>
              </InputText>
              <InputText
                v-if="showOldPoBox"
                ref="oldPoBox"
                v-model="oldPoBoxNumber"
                label="Po Box"
                name="old-po-box"
                placeholder="PO Box 123"
                class="mt-5"
                :has-error="errors.oldPoBoxNumber.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldPoBox"
                @input="oldAddressError = ''"
              >
                <span slot="helper">
                  {{ errors.oldPoBoxNumber.message }}
                </span>
              </InputText>
            </div>
          </section>
          <div class="c-hairline" />
          <section
            class="flex py-8"
          >
            <div class="w-1/2 max:pr-xl">
              <Tooltip
                placement="top"
                @show="analytics.click.tooltipNewAddress"
              >
                <h3 class="font-bold">
                  New Mailing Address
                </h3>
                <template slot="tip">
                  <p>
                    This is the address you're moving to. Enter a valid address,
                    and remember to include apartment/suite number if applicable.
                  </p>
                  <p
                    class="mt-5"
                  >
                    If your address is a PO Box, enter it in the Street
                    Address field for example, PO Box 1001. Please note
                    that Change-of-Address request from non-USPS Private
                    Mail Boxes (PMB) cannot be processed.
                  </p>
                  <p
                    v-if="newPoBoxException"
                    class="errors text-red-200 mt-5"
                  >
                    <span class="font-bold">Note:</span> The address you
                    provided is not considered a proper mailing address
                    within this ZIP Code™. A proper mailing address within
                    this ZIP Code™ requires a PO Box address. Please input
                    your PO Box number in the “PO Box” field and click "Next"
                    to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
                  </p>
                </template>
              </Tooltip>
              <InputZipCode
                ref="newZipCode"
                v-model="newZipCode"
                label="ZIP Code™"
                placeholder="00000"
                name="new-zip-code"
                class="mt-5"
                :choice-experience="true"
                :has-error="errors.newZipCode.hasError"
                :async-fn="zipSearch({
                  cityName: newCity,
                  state: newState,
                })"
                :transform-compare="zipCompare"
                :transform-label="zipLabel"
                :transform-value="zipValue"
                @blur="analytics.input.newZipCode"
                @select="newCity = $event.cityName;
                         newState = $event.state"
              >
                <template slot="helper">
                  {{ errors.newZipCode.message }}
                </template>
              </InputZipCode>
              <div class="choice__selections--unstack">
                <InputListBox
                  ref="newCity"
                  v-model="newCity"
                  label="City"
                  placeholder="City"
                  name="new-city"
                  class="mt-5"
                  :has-error="errors.newCity.hasError"
                  :async-fn="citySearch({
                    zip: newZipCode,
                    state: newState,
                  })"
                  :transform-compare="zipCompare"
                  :transform-label="cityLabel"
                  :transform-value="cityValue"
                  @blur="analytics.input.newCity"
                  @select="newZipCode = $event.zip; newState = $event.state"
                >
                  {{ errors.newCity.message }}
                </InputListBox>
                <InputState
                  ref="newState"
                  v-model="newState"
                  label="State"
                  name="new-state"
                  class="mt-5"
                  :has-error="errors.newState.hasError"
                  @input="analytics.input.newState()"
                >
                  <span
                    v-if="errors.newState.hasError"
                    slot="helper"
                  >
                    {{ errors.newState.message }}
                  </span>
                </InputState>
              </div>
              <InputListBox
                ref="newStreet"
                v-model="newStreetAddress"
                label="Street Address"
                name="new-street-address"
                placeholder="Street Address"
                class="mt-5"
                :has-error="errors.newStreetAddress.hasError && !showNewPoBox && !showNewPrivateMailbox"
                :async-fn="streetSearch({
                  cityName: newCity,
                  state: newState,
                  zipcode: newZipCode,
                })"
                :transform-compare="streetCompare"
                :transform-label="streetLabel"
                :transform-value="streetValue"
                @input="newPoBoxNumber = ''; newPrivateMailbox = '';"
                @blur="analytics.input.newStreet"
                @select="newZipCode=$event.zip;
                         newCity=$event.cityName;
                         newState=$event.state;"
              >
                {{ errors.newStreetAddress.message }}
              </InputListBox>
              <InputText
                v-if="newState === 'PR'"
                ref="newUrbanizationName"
                v-model="newUrbanizationName"
                label="Urbanization Name (Puerto Rico only)"
                name="new-urbanization-name"
                placeholder="URB HIGHLAND GDNS"
                class="mt-5"
                :has-error="errors.newUrbanizationName.hasError"
                @blur="analytics.input.newUrbanizationName"
              >
                <span slot="helper">
                  {{ errors.newUrbanizationName.message }}
                </span>
              </InputText>
              <InputText
                v-if="showNewPoBox"
                ref="newPoBox"
                v-model="newPoBoxNumber"
                label="Po Box"
                name="new-po-box"
                placeholder="PO Box 123"
                class="mt-5"
                :has-error="errors.newPoBoxNumber.hasError"
                @blur="analytics.input.newPoBox"
              >
                <template slot="helper">
                  {{ errors.newPoBoxNumber.message }}
                </template>
              </InputText>
              <InputText
                v-if="showNewPrivateMailbox"
                ref="newPrivateMailbox"
                v-model="newPrivateMailbox"
                label="Private Mailbox"
                name="new-private-mailbox"
                placeholder="PMB 123"
                class="mt-5"
                :has-error="errors.newPrivateMailbox.hasError"
                @blur="analytics.input.newPrivateMailbox"
              >
                <template slot="helper">
                  {{ errors.newPrivateMailbox.message }}
                </template>
              </InputText>
            </div>
          </section>
        </template>
        <section class="py-8">
          <div class="w-2/3">
            <p><strong>Request for Change-of-Address and Forwarding Services </strong></p>
            <p
              style="font-size: 14px"
              class="mt-2 mb-5"
            >
              <strong>Privacy Act Statement:</strong> Your information
              will be used to provide mail forwarding and Change-of-Address
              (COA) services and move related advertisements, to verify your
              identity, to facilitate communication, to identify and mitigate
              potential fraud and to provide address correction services.
              Collection is authorized by 39 U.S.C. 401, 403, 404. Providing
              the information is voluntary, but if not supplied, we may not be
              able to process and fulfill your request for services.
              <span
                v-if="!privacyActFullContent"
                class="font-bold"
                :class="['c-link--choice', 'c-link--inline','cursor-pointer']"
                tabindex="0"
                @click="togglePrivacyActContent"
                @keyup.enter="togglePrivacyActContent"
                @keyup.space="togglePrivacyActContent"
              >
                Click here to display the entire Privacy Act Statement
              </span>
              <template v-if="privacyActFullContent">
                We do not disclose your information to third parties without your
                consent, except to facilitate the transaction, to act on your behalf
                or request, or as legally required. This includes the following standard
                and specific routine uses: Incident to legal proceedings; for law enforcement
                purposes; to a congressional office; to agents or contractors; to auditors;
                to labor organizations; to federal, state, local, or foreign government
                agencies; to agencies and entities for financial matters; for customer service
                purposes; for voter registration to a duly formed election board or registration
                commission; to a federal, state, or local government agency upon certification
                when required for the performance of its duties; to a law enforcement agency
                after the Postal Inspection Service has confirmed that the COA information is
                needed for a criminal investigation; for service of process; for purpose of
                jury service; to agencies or entities at customer’s request; to a disaster
                relief organization; pursuant to the order of a court of competent jurisdiction;
                for address correction services only if the mailer is in possession of the name
                and old address; and to USPS licensed service providers to perform mailing list
                address correction services for lists containing your name and old address. For
                a list of licensed service providers please visit <a
                  href="https://postalpro.usps.com/NCOALink_Service_Providers"
                  :class="['c-link--choice', 'c-link--inline']"
                >https://postalpro.usps.com/NCOALink_Service_Providers</a>. For more
                information regarding our privacy policies visit <a
                  href="https://www.usps.com/privacypolicy"
                  target="_blank"
                  :class="['c-link--choice', 'c-link--inline']"
                  rel="noopener noreferrer"
                >www.usps.com/privacypolicy</a>.
                <button
                  :class="['c-link--choice', 'c-link--inline']"
                  type="button"
                  @click="togglePrivacyActContent"
                >
                  Display Less
                </button>
              </template>
            </p>
            <p v-if="twoFactorAuthEnabled">
              <strong>Identity Verification and Online Identity Proofing </strong>
            </p>
            <p
              v-if="twoFactorAuthEnabled"
              style="font-size: 14px"
              class="mt-2 mb-5"
            >
              <strong>Privacy Act Statement:</strong> Your information will be used to verify
              your identity using enhanced remote identity proofing when applying for
              Change-of-Address (COA) services online, to improve the security of the COA
              process, and to identify and mitigate potential fraud. Collection is authorized
              by 39 U.S.C. 401, 403, 404, and 411. Providing your information is voluntary,
              but if not provided, we may not be able to verify your identity online when
              applying for COA and mail forwarding services.
              <span
                v-if="!identityProofingFullContent"
                class="font-bold"
                :class="['c-link--choice', 'c-link--inline', 'cursor-pointer']"
                tabindex="0"
                @click="toggleIdentityProofingContent"
                @keyup.enter="toggleIdentityProofingContent"
                @keyup.space="toggleIdentityProofingContent"
              >
                Click here to display the entire Privacy Act Statement
              </span>
              <template v-if="identityProofingFullContent">
                We do not disclose your information without your consent to third parties,
                except for the following limited circumstances: incident to legal proceedings
                involving the Postal Service; for law enforcement purposes; to a congressional
                office on your behalf; to agents or contractors when necessary to fulfill a
                business function; to a U.S. Postal Service auditor; to labor organizations as
                required by applicable law; to government agencies in connection with decisions
                as necessary; to agencies and entities for financial matters; and for customer
                service purposes. For more information on our privacy policies visit <a
                  href="https://www.usps.com/privacypolicy"
                  target="_blank"
                  :class="['c-link--choice', 'c-link--inline']"
                  rel="noopener noreferrer"
                >www.usps.com/privacypolicy</a>.
                <button
                  :class="['c-link--choice', 'c-link--inline']"
                  type="button"
                  @click="toggleIdentityProofingContent"
                >
                  Display Less
                </button>
              </template>
            </p>
            <transition
              name="fade"
              appear
            >
              <section v-if="renderV3Challenge && !showOutageBanner">
                <p class="mb-4">
                  For your security, clicking the box below confirms that this
                  Change-of-Address form is being completed by a person, and not
                  an automated query.
                </p>
                <vue-recaptcha
                  id="recaptcha-challenge"
                  ref="recaptchaChallenge"
                  :sitekey="getRecaptchaV2Key()"
                  @click.native="analytics.click.recaptchaVerified"
                  @verify="recaptchaChallengeToken=$event;"
                  @expired="recaptchaChallengeToken='';"
                />
                <transition
                  appear
                  name="fade"
                >
                  <p
                    v-if="errors.recaptchaChallengeToken && errors.recaptchaChallengeToken.hasError"
                    class="c-input-text__helper c-input-helper errors"
                  >
                    {{ errors.recaptchaChallengeToken.message }}
                  </p>
                </transition>
              </section>
            </transition>
            <button
              v-if="!showOutageBanner"
              id="next"
              ref="nextBtn"
              :class="{ 'mt-5': renderV3Challenge }"
              class="c-btn"
              @click="analytics.click.next(); trackFirstEvent('next-btn');"
            >
              Next
            </button>
          </div>
        </section>
      </form>
      <div class="page-footer-section">
        <div class="mt-4 flex flex-col u-container--x">
          <p>
            <strong>Preparer Information:</strong>
            The person who prepares this form states that he or she is
            the person, executor, guardian, authorized officer, or agent
            of the person for whom mail would be forwarded under this order.
            Anyone submitting false or inaccurate information on this form
            is subject to punishment by fine or imprisonment or both under
            Sections 2, 1001, 1702, and 1708 of Title 18, United States Code.
          </p>

          <div class="flex mt-4 mb-4">
            <a
              href="https://about.usps.com/who/legal/privacy-policy/full-privacy-policy.htm"
              :class="['c-link--choice', 'c-link--inline']"
              target="_blank"
              rel="noopener noreferrer"
            >Privacy Policy</a>
            <router-link
              :to="{ name: 'terms-of-use' }"
              target="_blank"
              class="ml-4"
              :class="['c-link--choice', 'c-link--inline']"
            >
              <span>Terms of Use</span>
            </router-link>
          </div>
        </div>
      </div>
    </main>
    <Modal
      ref="newConstructionModal"
      v-model="newConstructionModal"
      close-id="new-constr-close-x"
    >
      <ModalNewConstruction
        :street-address="newStreetAddress"
        :city="newCity"
        :state="newState"
        :zip-code="newZipCode"
        :urbanization-name="newUrbanizationName"
        @reset="newConstructionModal = false"
        @override="newConstructionOverride"
      />
    </Modal>
    <Modal
      ref="familyModal"
      v-model="moverTypeFamilyModal"
    >
      <h4>Based on your responses...</h4>
      <p
        class="mt-3"
      >
        {{
          familySameLastName === 'YES' || familyRemaining === 'YES'
            ? `A Family move will forward all mail associated with this last name.
            Based on your response, an Individual Change-of-Address must be done for
            each person moving to ensure the mail of the person remaining is unaffected
            by this Change-of-Address.`
            : ''
        }}
        {{
          familySameLastName === 'NO' && familyRemaining !== 'YES'
            ? `A Family move is only required when more than one person,
            with the same last name, is moving from the address. Anyone
            with a different last name must file an Individual Change-of-Address.`
            : ''
        }}
      </p>
      <button
        v-if="familySameLastName === 'YES' || familyRemaining === 'YES'"
        class="c-btn mt-3"
        @click="moverType='INDIVIDUAL'; moverTypeFamilyModal=false;"
      >
        Continue
      </button>
      <button
        v-if="familySameLastName === 'NO' && familyRemaining !== 'YES'"
        class="c-btn mt-3"
        @click="moverTypeFamilyModal=false;"
      >
        Continue
      </button>
    </Modal>
    <Modal
      ref="businessModal"
      v-model="moverTypeBusinessModal"
    >
      <h4>Based on your responses...</h4>
      <p
        class="mt-3"
      >
        A Business move should only be submitted when the entire business
        is moving. If the entire business is moving to a new address, please
        select Continue, and change your response to Yes.
      </p>
      <button
        class="c-btn mt-3"
        @click="moverType='INDIVIDUAL'; moverTypeBusinessModal=false;"
      >
        Continue
      </button>
    </Modal>
    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address
        you've entered. To make changes to your current Change-of-Address,
        visit
        <a
          :class="['c-link--choice', 'c-link--inline']"
          :href="$options.coacUrl"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code,
        which can be located within your confirmation email and the
        Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </div>
</template>

<script>
/* eslint-disable max-len */
import VueRecaptcha from 'vue-recaptcha';
// MGO UI Components
import {
  Banner,
  InputListBox,
  InputRadio,
  InputState,
  InputText,
  InputZipCode,
  Modal,
  Tooltip,
  IndividualProgressBarWithSteps,
} from '@mgo/ui/lib/components';
import Cookies from 'js-cookie';

// mixin used in both Disclaimer/desktop.vue as well as MoveCombined/mobile.vue
import { desktopAnalytics } from './analytics';
import moveCombinedMixin from '../../choice-views/MoveCombined/mixin';
import addressViolations from '../../mixins/addressViolations';
import destinationManager from '../../mixins/destinationManager';
import grecaptcha from '../../mixins/grecaptcha';
import moverType from '../../mixins/moverType';
import newConstruction from '../../mixins/newConstruction';
import rapidKey from '../../mixins/rapidKey';
import ModalNewConstruction from '../../components/ModalNewConstruction.vue';
import SimplifyInputRadio from '../../components/SimplifyInputRadio.vue';
import SimplifyHero from '../../simplify/disclaimer/desktop.vue';
// SVG Vue Components
import SvgBusiness from '../../components/SvgBusiness.vue';
import SvgFamily from '../../components/SvgFamily.vue';
import SvgIndividual from '../../components/SvgIndividual.vue';

// SVG Image Imports
import SvgChangeOfAddress from '../../assets/images/_icon-change-of-address.svg';
import SvgMYMOVE from '../../assets/images/_icon-mymove.svg';
import SvgSavings from '../../assets/images/_icon-savings.svg';
import MultipleBusinessAddresses from '../../components/MultipleAddresses/desktop.vue';

import { redirectToHomePage } from '../../utils/commonUtil';

export default {
  name: 'DisclaimerSimplify',
  components: {
    Banner,
    IndividualProgressBarWithSteps,
    SimplifyHero,
    InputListBox,
    InputRadio,
    InputState,
    InputText,
    InputZipCode,
    Modal,
    ModalNewConstruction,
    SvgBusiness,
    SvgFamily,
    SvgIndividual,
    VueRecaptcha,
    SimplifyInputRadio,
    Tooltip,
    MultipleBusinessAddresses,
  },
  mixins: [
    addressViolations,
    destinationManager,
    grecaptcha,
    moveCombinedMixin,
    moverType,
    newConstruction,
    rapidKey,
  ],
  data() {
    return {
      identityProofingFullContent: false,
      privacyActFullContent: false,
      current: 1,
      emailConfirm: '',
    };
  },
  computed: {
    totalSteps() {
      return this.informedDeliveryEligible ? 7 : 6;
    },
    progressStep() {
      return this.current / this.totalSteps;
    },
    isFamily() {
      return true;
    },
    analytics() {
      return desktopAnalytics();
    },
    flagAmpersendEnabled() {
      return this.$store.get('flagAmpersendEnabled');
    },
    showOutageBanner() {
      return this.$store.get('flagOutageBanner');
    },
    showUpcomingMaintenanceBanner() {
      return this.$store.get('upcomingMaintenance');
    },
  },
  beforeRouteEnter(to, from, next) {
    /* RESET COA BEHAVIOR */
    const allowedReEntry = [
      'additional-services',
      'verify-info-read',
      'verify-info-edit',
      'identity-check',
      'cmra',
      'residential-business',
      'old-unique-zip',
      'fatal-error',
      'contact-info',
      'disclaimer',
    ];

    next((vm) => {
      if ((from.name && !allowedReEntry.includes(from.name)) || Cookies.get('coa_complete')) {
        vm.$deleteSession();
        vm.$coa.reset();
        Cookies.remove('coa');
        Cookies.remove('coa_complete');
      }
    });
  },
  created() {
    this.simplify = true;
    sessionStorage.setItem('showSimplifyExperience', true);
  },
  async mounted() {
    if (this.$store.get('config@twoFactorAuthEnabled') && this.$store.get('mfa@allowProfiling')) {
      this.deviceProfiling();
    }

    this.offersChoice = JSON.parse(sessionStorage.getItem('offersChoice') ?? false);
    this.autofillChoice = JSON.parse(sessionStorage.getItem('autofillChoice') ?? false);
    this.informedDelivery = JSON.parse(sessionStorage.getItem('informedDelivery'));
    if (Cookies.get('coa_complete')) {
      this.$deleteSession();
      this.$coa.reset();
      Cookies.remove('coa');
      Cookies.remove('coa_complete');
    }
    if (this.$coa.coaUUID) {
      sessionStorage.setItem('coaUUID', this.$coa.coaUUID);
      await this.hydrateCOA();
      await this.hydrateAddresses();
    } else {
      redirectToHomePage(this);
    }

    this.pageView();
    this.dirty = false;
  },
  methods: {
    togglePrivacyActContent() {
      this.privacyActFullContent = !this.privacyActFullContent;
    },
    toggleIdentityProofingContent() {
      this.identityProofingFullContent = !this.identityProofingFullContent;
    },
    showBusinessAliasHandle() {
      this.showBusinessAlias = !this.showBusinessAlias;
    },
    source(src) {
      return `https://cdn.ampersend.io/${this.toggleImage(src)}.png`;
    },
    toggleImage(src) {
      return src === 'disclaimer-page-autofill' ? '26Wl9n6yfMhCZaPOJgjywXw1FYn' : '26Wl9oSDj38ujOU6FhdZIE7LAV0';
    },
    async submit() {
      this.improvedFieldErrors = {};
      sessionStorage.setItem('offersChoice', this.offersChoice);
      sessionStorage.setItem('autofillChoice', this.autofillChoice);
      sessionStorage.setItem('informedDeliveryEligible', this.informedDeliveryEligible);
      sessionStorage.setItem('startDate', this.startDate);
      sessionStorage.setItem('temp', this.forwardType === 'TEMPORARY');
      if (this.showOutageBanner) return;
      this.$store.commit('ENABLE_LOADER');

      if (this.twoFactorAuthEnabled) {
        this.phoneType = 'MOBILE';
      }
      const { xhr } = await this.$coa.coaUpdateV2({
        moverType: this.moverType,
        famPrompt1ShareLastName: this.familySameLastName
          ? this.familySameLastName === 'YES'
          : null,
        famPrompt2SameLastNameRemaining: this.familyRemaining
          ? this.familyRemaining === 'YES'
          : null,
        bizPromptEntireMoving: this.businessWholeCompany
          ? this.businessWholeCompany === 'YES'
          : null,
        multipleAddresses: this.multipleAddresses === 'YES' ?? false,
      });

      this.attachFieldErrors({ xhr });

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      const addressResponses = await this.$coa.addressSaveV2Simplify({
        addressType: 'OLD',
        streetAddress: this.oldStreetAddress,
        city: this.oldCity,
        state: this.oldState,
        zipCode: this.oldZipCode,
        urbanizationName: this.oldState === 'PR'
          ? this.oldUrbanizationName
          : null,
        privateMailbox: null,
        poBoxNumber: this.oldPoBoxNumber || null,
        recentConstruction: false,
        overrideAms: false,
        moverType: this.moverType,
      }, {
        addressType: 'NEW',
        streetAddress: this.newStreetAddress,
        city: this.newCity,
        state: this.newState,
        zipCode: this.newZipCode,
        urbanizationName: this.newState === 'PR'
          ? this.newUrbanizationName
          : null,
        privateMailbox: this.newPrivateMailbox || null,
        poBoxNumber: this.newPoBoxNumber || null,
        recentConstruction: this.recentConstruction,
        overrideAms: this.overrideAms,
        moverType: this.moverType,
      });


      if (addressResponses?.xhr?.exception) {
        if (addressResponses?.xhr?.exception === 'MilitaryZip5MoverTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'moverType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.moverType.message = addressResponses?.xhr?.message || '';
          this.errors.moverType.hasError = true;
        } else if (addressResponses?.xhr?.addressType === 'NEW') {
          this.newAddressError = addressResponses?.xhr?.exception || '';
          this.errors.newStreetAddress.hasError = true;
          this.attachFieldErrors(this.newAddressError, 'new');
        } else {
          this.oldAddressError = addressResponses?.xhr?.exception || '';
          this.errors.oldStreetAddress.hasError = true;
          this.attachFieldErrors(this.oldAddressError, 'old');
        }
      }
      if (addressResponses?.xhr?.exception === 'InvalidPrimaryPoBoxException' && this.errors?.newPoBoxNumber?.hasError) {
        this.errors.newStreetAddress.hasError = false;
        this.errors.newStreetAddress.message = '';
      }
      if (addressResponses?.xhr?.fieldErrors && /new/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.newAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.newAddressError, 'new');
      }
      if (addressResponses?.xhr?.fieldErrors && /old/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.oldAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.oldAddressError, 'old');
      }
      if (addressResponses?.xhr?.fieldErrors) {
        // eslint-disable-next-line no-unused-expressions
        addressResponses?.xhr?.fieldErrors.forEach((fieldError) => {
          const fieldNameError = fieldError.field?.split('.')[1].charAt(0).toUpperCase() + fieldError.field?.split('.')[1].slice(1);
          const oldAddressField = `old${fieldNameError}`;
          const newAddressField = `new${fieldNameError}`;
          if (fieldError.field.indexOf('newAddress') === 0) {
            this.errors[newAddressField].message = fieldError.message;
            this.errors[newAddressField].hasError = true;
          } else {
            this.errors[oldAddressField].message = fieldError.message;
            this.errors[oldAddressField].hasError = true;
          }
        });
      }

      if (this.oldAddressError === 'OldAndNewAddressMatchException'
         || this.newAddressError === 'OldAndNewAddressMatchException') {
        this.newAddressError = 'OldAndNewAddressMatchException';
        this.oldAddressError = 'OldAndNewAddressMatchException';
      }

      if (this.oldAddressError === 'MilitaryAddressException'
        || this.newAddressError === 'MilitaryAddressException') {
        this.newAddressError = 'MilitaryAddressException';
        this.oldAddressError = 'MilitaryAddressException';
      }

      // log the defaultExceptions for old/new address
      if (!addressResponses?.xhr?.fieldErrors) {
        if (this.oldAddressError) {
          this.defaultException(this.oldAddressError);
        }
        if (this.newAddressError) {
          this.defaultException(this.newAddressError);
        }
      }
      const errorPageRedirect = this.evaluateErrorRedirect(addressResponses);
      if (errorPageRedirect) {
        this.$store.commit('DISABLE_LOADER');
        this.$router.push({ name: errorPageRedirect });
        return;
      }

      if (!addressResponses?.NEW?.uuid) {
        this.newConstructionEvaluateAddress(addressResponses);
      }

      if (this.newConstructionAttempts > 0) {
        this.$store.commit('DISABLE_LOADER');
        return;
      }

      // log the amsStatusCodes on 200 responses for old/new addresses
      if (addressResponses?.addressType
         && this.isAmsMatch(addressResponses?.amsStatusCode)) {
        this.defaultException(addressResponses.amsStatusCode);
      }

      if (this.flagAmpersendEnabled) {
        this.$amp.identify({
          mgoMoverType: this.moverType,
          mgoOldZip5: this.oldZipCode,
          mgoNewZip5: this.newZipCode,
          offersOptIn: JSON.parse(sessionStorage.getItem('mmco')) ?? this.offersChoice,
          voterregOptIn: JSON.parse(sessionStorage.getItem('mmcvr')) ?? this.autofillChoice,
        });
      }

      // Add Email domain start/stop banner flags to query string if API updated start/stop dates
      // Pass exception from response in case errors are cleared from a double submit
      this.next(this.$route.query, addressResponses?.xhr?.exception);
    },
  },
  SvgChangeOfAddress,
  SvgMYMOVE,
  SvgSavings,
  validateConfirmEmail: true,
};
</script>
<style>
@import '../../css/components/choice-selections.css';

</style>
